import React from 'react';
import styled, { css } from 'styled-components';
import { theme, toRem } from 'design';
import { ButtonIcon } from '../Button';

interface ActionMenuProps {
  width?: number;
  isVisible?: boolean;
  tabIndex?: number;
  onClose?: () => void;
  children?: React.ReactNode;
  className?: string;
  hideMobileHeader?: boolean;
}

const Container = styled.div<Partial<ActionMenuProps>>`
  position: absolute;
  top: 0;
  left: 0;
  z-index: ${theme.zIndex.dialog};
  display: none;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: 100%;
  background-color: ${theme.color.white};

  ${({ isVisible }) =>
    isVisible &&
    css`
      display: flex;
    `}

  @media (min-width: ${theme.breakpoint.small}px) {
    display: none;
    visibility: hidden;
    width: 0;
    height: 0;
    border-radius: ${toRem(11)};
    background-color: ${theme.color.white};
    box-shadow: ${theme.shadow.dialog};
    overflow: hidden;
    transition: width ${theme.transition.button.default};

    ${({ isVisible }) =>
      isVisible &&
      css`
        display: flex;
        visibility: visible;
        width: auto;
        min-width: ${toRem(180)};
        height: auto;
      `}

    ${({ width }) =>
      width &&
      css`
        width: ${toRem(width)};
      `}
  }
`;

const Layout = styled.div<Partial<ActionMenuProps>>`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: 100%;

  @media (min-width: ${theme.breakpoint.small}px) {
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-start;
    align-items: flex-start;
    width: 100%;
    height: auto;
  }
`;

const MobileHeader = styled.header`
  flex-shrink: 0;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  height: ${toRem(theme.header.height)};
  padding: 0 ${toRem(theme.space.xl)};

  @media (min-width: ${theme.breakpoint.small}px) {
    display: none;
  }
`;

const ActionMenu: React.FC<ActionMenuProps> = ({ width, hideMobileHeader = false, isVisible, tabIndex, onClose, children, className, ...rest }) => (
  <Container width={width} isVisible={isVisible} className={className} {...rest}>
    {!hideMobileHeader && (
      <MobileHeader>
        <ButtonIcon label="Close." appearance="secondary" size="small" icon="Close" onClick={onClose} />
      </MobileHeader>
    )}
    <Layout tabIndex={tabIndex}>{children}</Layout>
  </Container>
);

export default ActionMenu;
